import type { ReactNode } from 'react';
import { AnimateTransform, useSmilTimeline, type Keys } from '../smil';

/** The whole build plays this much slower than its beats are written, so each step can be followed. */
const PACE = 1.1;

export function paced(seconds: number) {
	return Number((seconds * PACE).toFixed(4));
}

/** Seconds: the fist starts to open here, uncovering the small card it held. */
export const FIST_OPENS = paced(0.2);
/** Seconds: the card has grown into its place in the open hand, and the first sparkle pops. */
export const LAND = paced(1.1);
export const LINE = { stroke: '#25302C', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

/** A scale about `centre` instead of the origin: SMIL has no transform-origin. */
export function ScaleAbout(props: { centre: string; keys: Keys; children: ReactNode }) {
	const [x, y] = props.centre.split(' ');
	return (
		<g transform={`translate(${props.centre})`}>
			<g>
				<AnimateTransform type="scale" keys={props.keys} />
				<g transform={`translate(-${x} -${y})`}>{props.children}</g>
			</g>
		</g>
	);
}

export type Frames = readonly (readonly [seconds: number, value: string | number])[];

/** Linear between frames sampled densely enough to carry their own easing; one pass or a loop, as the timeline. */
export function LinearTrack(props: { attr: string; type?: 'scale'; frames: Frames }) {
	const timeline = useSmilTimeline();
	if (!timeline.enabled) return null;
	const first = props.frames[0];
	const last = props.frames.at(-1);
	if (!first || !last) return null;
	const all = [
		...(first[0] > 0 ? [[0, first[1]] as const] : []),
		...props.frames,
		...(last[0] < timeline.seconds ? [[timeline.seconds, last[1]] as const] : []),
	];
	const common = {
		dur: `${timeline.seconds}s`,
		...(timeline.once ? { fill: 'freeze' } : { repeatCount: 'indefinite' }),
		keyTimes: all.map(([t]) => (t / timeline.seconds).toFixed(4)).join(';'),
		values: all.map(([, value]) => value).join(';'),
	};
	if (props.type) return <animateTransform attributeName="transform" type={props.type} {...common} />;
	return <animate attributeName={props.attr} {...common} />;
}

export function times(from: number, to: number, step: number) {
	return Array.from({ length: Math.round((to - from) / step) + 1 }, (_, i) => Number((from + i * step).toFixed(4)));
}
